import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUturnLeftIcon, CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField } from './fields';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import {
  ChargeCodeLine, DefaultMapLine, EdiChargeCodeOptions, RemovedChargeCodeLine,
  ediChargeCodesHistoryEndpoint, getEdiChargeCodeDefaultMap, getEdiChargeCodeLines,
  getEdiChargeCodeOptions, saveEdiChargeCodeLines,
} from '../../services/ediChargeCodes.service';

/**
 * Bespoke edit/create drawer for EDI Charge Code Mapping (cms.edi_chargecodemap) —
 * one row per (EDI handle, invoice line-item type) -> charge code on the outbound
 * 210, presented GROUPED by handle. The drawer edits one handle's whole set: pick
 * the EDI Handle, then add/edit/remove Line Item -> Charge Code rows. Save
 * reconciles the set via PUT /edi-chargecodes/lines (insert / reactivate / update /
 * soft-delete). On edit the handle is fixed (it's the group key).
 *
 * A handle stores only its OVERRIDES — anything it does not name it inherits from
 * the DEFAULT handle's base map — so each row shows the default code beside the
 * override, and a collapsible panel lists the whole default map for reference.
 * Editing DEFAULT itself is the same screen without that context.
 *
 * A BLANK charge code is a real configuration ("send this line with no code"), not
 * an unfinished row, so blank saves without complaint. Codes uppercase on blur; the
 * endpoint uppercases too, since it is the authority.
 *
 * Removals are soft, and "Show removed" lists them with a restore. Restoring moves
 * the row back into the live set carrying its original recno, so the save
 * reactivates that record rather than inserting a second one for the pair — the
 * mapping keeps one identity and one history.
 *
 * Replaces the hardcoded %lineitemmap in V1's lib_invoice.pl initEDIvars.
 */
const DEFAULT_HANDLE = 'DEFAULT';
const CODE_MAX = 20;

const s = (v: any): string => (v == null ? '' : String(v));

/** A row being edited. `recno` 0 = added here and not yet saved. */
interface Row {
  recno: number;
  litype_recno: string;   // string: the selects are string-valued
  chargecode: string;
  litype_name: string;
  litype_deleted: string;
  default_code?: string | null;
}

const toRow = (l: ChargeCodeLine): Row => ({
  recno: l.recno,
  litype_recno: s(l.litype_recno),
  chargecode: s(l.chargecode),
  litype_name: s(l.litype_name),
  litype_deleted: s(l.litype_deleted),
  default_code: l.default_code,
});

const LockedField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-muted mb-1">{label}</label>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {children}
    </div>
  </div>
);

const EdiChargeCodeMapEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;

  const [opts, setOpts] = useState<EdiChargeCodeOptions | null>(null);
  const [defaultMap, setDefaultMap] = useState<DefaultMapLine[] | null>(null);
  const [edihandle, setEdihandle] = useState(s(row?.edihandle));
  const [rows, setRows] = useState<Row[]>([]);
  const [removed, setRemoved] = useState<RemovedChargeCodeLine[]>([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [deletes, setDeletes] = useState<number[]>([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [showDefaults, setShowDefaults] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const isDefaultHandle = edihandle.trim() === DEFAULT_HANDLE;

  useEffect(() => {
    getEdiChargeCodeOptions().then(setOpts).catch(() => {});
    getEdiChargeCodeDefaultMap().then(setDefaultMap).catch(() => {});
  }, []);

  // Load the chosen handle's current overrides (both modes) so picking an
  // already-mapped handle merges into its set rather than starting empty.
  useEffect(() => {
    const h = edihandle.trim();
    if (!h) { setRows([]); setRemoved([]); setDeletes([]); return; }
    let alive = true;
    setLinesLoading(true);
    getEdiChargeCodeLines(h)
      .then(({ lines, removed: gone }) => {
        if (!alive) return;
        setRows(lines.map(toRow));
        setRemoved(gone);
        setDeletes([]);
      })
      .catch(() => { if (alive) { setRows([]); setRemoved([]); setDeletes([]); } })
      .finally(() => { if (alive) setLinesLoading(false); });
    return () => { alive = false; };
  }, [edihandle]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  // Add mode: hide handles that already have a mapping (edit those from the grid)
  // so we don't create a second group for the same handle.
  const mappedHandles = useMemo(() => new Set(opts?.mapped_handles ?? []), [opts]);
  const addHandleOpts = useMemo(
    () => (opts?.edihandles ?? []).filter((o) => !mappedHandles.has(o.value)),
    [opts, mappedHandles],
  );

  const litypeOpts = useMemo(() => opts?.litypes ?? [], [opts]);
  const litypeName = useMemo(() => {
    const m = new Map<string, string>();
    litypeOpts.forEach((o) => m.set(o.value, o.label));
    return m;
  }, [litypeOpts]);
  const defaultCodeFor = useMemo(() => {
    const m = new Map<string, string>();
    (defaultMap ?? []).forEach((d) => m.set(s(d.litype_recno), d.chargecode));
    return m;
  }, [defaultMap]);

  /** Options for one row's Line Item select: the full list minus types already used
   *  on OTHER rows, so the same line item can't be mapped twice for this handle. */
  const rowLitypeOpts = useCallback((idx: number) => {
    const used = new Set(rows.filter((_, i) => i !== idx).map((r) => r.litype_recno).filter(Boolean));
    const mine = rows[idx]?.litype_recno ?? '';
    const list = litypeOpts.filter((o) => !used.has(o.value));
    // A row pointing at a RETIRED line-item type isn't in the active option list;
    // keep it selectable so opening the drawer doesn't silently retarget the row.
    if (mine && !list.some((o) => o.value === mine)) {
      const name = rows[idx]?.litype_name || litypeName.get(mine) || `#${mine}`;
      return [{ value: mine, label: `${name} (deleted)` }, ...list];
    }
    return list;
  }, [rows, litypeOpts, litypeName]);

  const setRowField = (idx: number, key: 'litype_recno' | 'chargecode', value: string) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));

  const upperRow = (idx: number) =>
    setRows((prev) => prev.map((r, i) => (
      i === idx ? { ...r, chargecode: r.chargecode.trim().toUpperCase() } : r)));

  const addRow = () => {
    setFilter('');   // a new blank row must not land behind an active filter
    setRows((prev) => [...prev, {
      recno: 0, litype_recno: '', chargecode: '', litype_name: '', litype_deleted: '',
    }]);
  };

  const removeRow = (idx: number) => {
    const target = rows[idx];
    if (target?.recno) {
      setDeletes((prev) => (prev.includes(target.recno) ? prev : [...prev, target.recno]));
      // Show it in the removed list straight away, so an accidental delete is
      // undoable in the same visit rather than after a save-and-reopen.
      setRemoved((prev) => (prev.some((r) => r.recno === target.recno) ? prev : [...prev, {
        recno: target.recno,
        litype_recno: Number(target.litype_recno),
        litype_name: target.litype_name || (litypeName.get(target.litype_recno) ?? ''),
        litype_deleted: target.litype_deleted,
        chargecode: target.chargecode,
        default_code: target.default_code,
      }]));
    }
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  /** Move a removed mapping back into the live set, carrying its recno so the save
   *  reactivates that record. Also drops it from `deletes` — restoring something
   *  removed earlier in this same visit must cancel the pending delete. */
  const restoreRow = (recno: number) => {
    const target = removed.find((r) => r.recno === recno);
    if (!target) return;
    setDeletes((prev) => prev.filter((d) => d !== recno));
    setRemoved((prev) => prev.filter((r) => r.recno !== recno));
    setRows((prev) => [...prev, toRow(target)]);
    setFilter('');
  };

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!edihandle.trim()) e.push('EDI Handle is required.');
    if (rows.some((r) => !r.litype_recno)) e.push('Every row needs a Line Item.');
    const counts = new Map<string, number>();
    rows.forEach((r) => { if (r.litype_recno) counts.set(r.litype_recno, (counts.get(r.litype_recno) ?? 0) + 1); });
    [...counts.entries()].filter(([, n]) => n > 1).forEach(([recno]) => {
      e.push(`${litypeName.get(recno) ?? 'A line item'} is mapped more than once.`);
    });
    rows.forEach((r) => {
      if (r.chargecode.trim().length > CODE_MAX) {
        e.push(`Charge Code for ${litypeName.get(r.litype_recno) ?? 'a line item'} is longer than ${CODE_MAX} characters.`);
      }
    });
    return e;
  }, [edihandle, rows, litypeName]);

  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      await saveEdiChargeCodeLines(
        edihandle.trim(),
        rows.map((r) => ({
          recno: r.recno,
          litype_recno: Number(r.litype_recno),
          chargecode: r.chargecode.trim().toUpperCase(),
        })),
        deletes,
      );
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save EDI charge code mapping.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, edihandle, rows, deletes, onClose, onSaved]);

  const visibleRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const withIdx = rows.map((r, idx) => ({ r, idx }));
    if (!q) return withIdx;
    return withIdx.filter(({ r }) => {
      const name = (r.litype_name || litypeName.get(r.litype_recno) || '').toLowerCase();
      return name.includes(q) || r.chargecode.toLowerCase().includes(q);
    });
  }, [rows, filter, litypeName]);

  const cellCls = 'w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white disabled:opacity-50';
  const saveLabel = isNew && rows.length === 0 && deletes.length === 0 ? 'Create Mapping' : 'Save Changes';

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {saveLabel}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add EDI Charge Code Mapping' : `Edit EDI Charge Codes — ${s(row?.edihandle)}`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="52rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {edihandle.trim() && (
          <div className="flex justify-end">
            <EditHistoryLink endpoint={ediChargeCodesHistoryEndpoint(edihandle.trim())} label={edihandle.trim()} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isNew ? (
            <SelectField label="EDI Handle" required value={edihandle}
              onChange={setEdihandle} options={addHandleOpts} disabled={isSaving} />
          ) : (
            <LockedField label="EDI Handle">{s(row?.edihandle) || <span className="text-gray-400">—</span>}</LockedField>
          )}
          {!isNew && (
            <LockedField label="Bill To">
              {row?.billtoname ? titleCase(s(row.billtoname)) : <span className="text-gray-400">—</span>}
            </LockedField>
          )}
        </div>

        {isDefaultHandle ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 p-3 text-sm text-blue-900 dark:text-blue-200">
            This is the <strong>base map</strong>. Every EDI handle uses these codes for any
            line item it does not override, so a change here affects every partner.
          </div>
        ) : edihandle.trim() && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm text-muted">
            List only what this handle does <strong>differently</strong>. Any line item not listed
            here uses the DEFAULT code. Leave a Charge Code blank to send that line with no code.
          </div>
        )}

        {/* Line Item -> Charge Code rows. */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">
            {isDefaultHandle ? 'Default Charge Codes' : 'Charge Code Overrides'}
            {rows.length > 0 && <span className="ml-1 font-normal text-muted">({rows.length})</span>}
          </legend>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            {rows.length > 8 ? (
              <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter line items…" disabled={isSaving}
                className="w-full md:w-72 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
            ) : <span />}
            {removed.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-muted cursor-pointer select-none">
                <input type="checkbox" checked={showRemoved} disabled={isSaving}
                  onChange={(e) => setShowRemoved(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                Show removed ({removed.length})
              </label>
            )}
          </div>

          <div className="overflow-x-auto max-h-[26rem] overflow-y-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left text-muted">
                  <th className="py-1 pr-2 font-medium">Line Item</th>
                  {!isDefaultHandle && <th className="py-1 px-2 font-medium w-[110px]">Default</th>}
                  <th className="py-1 px-2 font-medium w-[140px]">Charge Code</th>
                  <th className="py-1 pl-2" />
                </tr>
              </thead>
              <tbody>
                {linesLoading ? (
                  <tr><td colSpan={4} className="py-2 text-muted">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={4} className="py-2 text-gray-400 dark:text-gray-500">
                    {!edihandle.trim() ? 'Choose an EDI Handle first.'
                      : removed.length > 0
                        ? `No live charge codes — ${removed.length} removed.`
                        : 'No charge codes yet.'}
                  </td></tr>
                ) : visibleRows.length === 0 ? (
                  <tr><td colSpan={4} className="py-2 text-gray-400 dark:text-gray-500">
                    No rows match “{filter.trim()}”.
                  </td></tr>
                ) : visibleRows.map(({ r, idx }) => {
                  const dflt = r.default_code ?? defaultCodeFor.get(r.litype_recno);
                  return (
                    <tr key={r.recno || `new-${idx}`} className="border-t border-gray-100 dark:border-gray-700/60 align-top">
                      <td className="py-1.5 pr-2">
                        <SelectField label="Line Item" hideLabel compact showSearch value={r.litype_recno}
                          onChange={(v) => setRowField(idx, 'litype_recno', v)}
                          options={rowLitypeOpts(idx)} disabled={isSaving}
                          invalid={showErrors && !r.litype_recno} />
                      </td>
                      {!isDefaultHandle && (
                        <td className="py-1.5 px-2 text-muted whitespace-nowrap">
                          {/* '' is a real default ("no code"), so it reads as (blank) rather
                              than as "DEFAULT has nothing for this line item" (—). */}
                          {dflt === undefined ? <span className="text-gray-400">—</span>
                            : dflt === '' ? <span className="italic text-gray-400">(blank)</span>
                            : dflt}
                        </td>
                      )}
                      <td className="py-1.5 px-2">
                        <input type="text" value={r.chargecode} disabled={isSaving} maxLength={CODE_MAX}
                          onChange={(e) => setRowField(idx, 'chargecode', e.target.value)}
                          onBlur={() => upperRow(idx)}
                          placeholder="blank = no code"
                          className={`${cellCls} uppercase`} />
                      </td>
                      <td className="py-1.5 pl-2 text-right">
                        <button type="button" onClick={() => removeRow(idx)} disabled={isSaving}
                          className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Remove charge code">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {showRemoved && removed.length > 0 && (
                  <>
                    <tr className="border-t border-gray-200 dark:border-gray-700">
                      <td colSpan={isDefaultHandle ? 3 : 4}
                        className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                        Removed
                      </td>
                    </tr>
                    {removed.map((r) => (
                      <tr key={`removed-${r.recno}`}
                        className="border-t border-gray-100 dark:border-gray-700/60 align-top opacity-60">
                        <td className="py-1.5 pr-2">
                          <div className="text-label line-through">
                            {r.litype_name || litypeName.get(s(r.litype_recno)) || `#${r.litype_recno}`}
                          </div>
                          {(r.removed_by || r.removed_at) && (
                            <div className="text-xs text-muted">
                              removed by {r.removed_by || 'unknown'}{r.removed_at ? ` · ${r.removed_at}` : ''}
                            </div>
                          )}
                        </td>
                        {!isDefaultHandle && (
                          <td className="py-1.5 px-2 text-muted whitespace-nowrap">
                            {(() => {
                              const d = r.default_code ?? defaultCodeFor.get(s(r.litype_recno));
                              return d === undefined ? <span className="text-gray-400">—</span>
                                : d === '' ? <span className="italic text-gray-400">(blank)</span> : d;
                            })()}
                          </td>
                        )}
                        <td className="py-1.5 px-2 text-muted line-through whitespace-nowrap">
                          {r.chargecode === '' ? <span className="italic text-gray-400">(blank)</span> : r.chargecode}
                        </td>
                        <td className="py-1.5 pl-2 text-right">
                          <button type="button" onClick={() => restoreRow(r.recno)} disabled={isSaving}
                            className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Restore charge code">
                            <ArrowUturnLeftIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>

          <button type="button" onClick={addRow} disabled={isSaving || !edihandle.trim()}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50">
            <PlusIcon className="w-4 h-4" /> Add charge code
          </button>
        </fieldset>

        {/* The base map, for reference while overriding it. */}
        {!isDefaultHandle && (defaultMap?.length ?? 0) > 0 && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700">
            <button type="button" onClick={() => setShowDefaults((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-label">
              <span>Default map ({defaultMap?.length})</span>
              <span className="text-muted">{showDefaults ? 'Hide' : 'Show'}</span>
            </button>
            {showDefaults && (
              <div className="max-h-64 overflow-y-auto border-t border-gray-200 dark:border-gray-700 px-3 py-2">
                <table className="w-full text-sm">
                  <tbody>
                    {defaultMap?.map((d) => (
                      <tr key={d.litype_recno} className="border-b border-gray-100 dark:border-gray-700/60 last:border-0">
                        <td className="py-1 pr-2 text-label">{d.litype_name}</td>
                        <td className="py-1 pl-2 text-right text-muted whitespace-nowrap">
                          {d.chargecode === '' ? <span className="italic text-gray-400">(blank)</span> : d.chargecode}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default EdiChargeCodeMapEditDrawer;
